import { Theme } from '@/styles/Theme';
import type { IsActiveWorker } from '@/types/company.types';
import { useState } from 'react';
import styled from '@emotion/styled';
import SelectWorkerActionModal from '../../../ManageWorkerPaymentModal/SelectWorkerActionModal';
import UnsubscribeWorkersModal from '../../../ManageWorkerPaymentModal/UnsubscribeWorkersModal';
import DeleteWorkerModal from '../../../ManageWorkerPaymentModal/DeleteWorkerModal';
import UndoUnsubscribeWorkersModal from '../../../ManageWorkerPaymentModal/UndoUnsubscribeWorkersModal';
import SubscribeWorkersModal from '../../../ManageWorkerPaymentModal/SubscribeWorkersModal';
import useDeleteWorkerImmediate from '@/hooks/useDeleteWorkerImmediate';
import usePaymentInfo from '@/hooks/usePaymentInfo';
import useCompanyInfo from '@/hooks/useCompanyInfo';

interface Props extends IsActiveWorker {
  workerId: string;
}

const WorkerActiveManageBtn = ({ workerId, isActive }: Props) => {
  const { data: paymentData } = usePaymentInfo();
  const { data: companyData } = useCompanyInfo();
  const subscriptionStatus = companyData?.data.subscriptionStatus || 'free';
  
  // Count active workers (isActive === '활성') - workers with auto_renew = true
  const activeWorkersCount = (paymentData?.data.workerList || []).filter(
    (w) => w.isActive === '활성'
  ).length;
  
  // Check if this is the last active worker
  const isLastActiveWorker = isActive === '활성' && activeWorkersCount === 1;
  const [isShowSelectActionModal, setIsShowSelectActionModal] = useState(false);
  const [isShowUnsubscribeModal, setIsShowUnsubscribeModal] = useState(false);
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [isShowActiveManageModal, setIsShowActiveManageModal] = useState(false);
  const [isShowUndoUnsubscribeModal, setIsShowUndoUnsubscribeModal] = useState(false);
  
  const { mutate: deleteWorkerImmediate } = useDeleteWorkerImmediate();

  const handleSelectDisableAutoRenew = () => {
    setIsShowSelectActionModal(false);
    setIsShowUnsubscribeModal(true);
  };

  const handleSelectDeleteWorker = () => {
    setIsShowSelectActionModal(false);
    setIsShowDeleteModal(true);
  };

  const handleSelectUndoUnsubscribe = () => {
    setIsShowSelectActionModal(false);
    setIsShowUndoUnsubscribeModal(true);
  };

  const handleConfirmDelete = () => {
    deleteWorkerImmediate({ workerId: [Number(workerId)] });
    setIsShowDeleteModal(false);
  };

  return (
    <>
      {isShowSelectActionModal && (
        <SelectWorkerActionModal
          setIsShowModal={setIsShowSelectActionModal}
          selectedWorkers={[Number(workerId)]}
          onSelectDisableAutoRenew={handleSelectDisableAutoRenew}
          onSelectDeleteWorker={handleSelectDeleteWorker}
          onSelectUndoUnsubscribe={handleSelectUndoUnsubscribe}
          workerStatus={isActive === '활성' || isActive === '비활성 예정' ? isActive : '활성'}
          isLastActiveWorker={isLastActiveWorker}
          subscriptionStatus={subscriptionStatus}
          activeWorkerCount={activeWorkersCount}
        />
      )}
      {isShowUnsubscribeModal && (
        <UnsubscribeWorkersModal
          setIsShowModal={setIsShowUnsubscribeModal}
          selectedWorkers={[Number(workerId)]}
          resetSelectedUnsubscribeWorkerList={() => {}}
        />
      )}
      {isShowDeleteModal && (
        <DeleteWorkerModal
          setIsShowModal={setIsShowDeleteModal}
          selectedWorkers={[Number(workerId)]}
          onConfirmDelete={handleConfirmDelete}
        />
      )}
      {isShowUndoUnsubscribeModal && (
        <UndoUnsubscribeWorkersModal
          setIsShowModal={setIsShowUndoUnsubscribeModal}
          selectedWorkers={[Number(workerId)]}
        />
      )}
      {isShowActiveManageModal && (
        (isActive === '비활성' || isActive === '복구 가능') && (
          <SubscribeWorkersModal
            setIsShowModal={setIsShowActiveManageModal}
            selectedAddWorkerList={[]}
            selectedRestoreWorkerList={[Number(workerId)]}
          />
        )
      )}
      {isActive !== '생성' && (
        <ManageBtn
          isActive={isActive}
          onClick={() => {
            if (isActive === '활성') {
              setIsShowSelectActionModal(true);
            } else if (isActive === '비활성 예정') {
              // Show modal to choose: Cancel Stop or Delete Worker
              setIsShowSelectActionModal(true);
            } else if (isActive === '비활성' || isActive === '복구 가능') {
              // Show restore modal for both 비활성 and 복구 가능
              setIsShowActiveManageModal(true);
            }
          }}
        >
          {isActive === '활성'
            ? '중단'
            : isActive === '비활성' || isActive === '복구 가능'
            ? '복구'
            : isActive === '비활성 예정' && '중단 취소'}
        </ManageBtn>
      )}
    </>
  );
};

export default WorkerActiveManageBtn;

export const ManageBtn = styled.button<IsActiveWorker>`
  background-color: ${({ isActive }) =>
    isActive === '활성'
      ? Theme.colors.WHITE
      : isActive === '비활성' || isActive === '복구 가능'
      ? Theme.colors.GREEN
      : isActive === '비활성 예정' && Theme.colors.WARNING_RED};
  border: ${({ isActive }) =>
    isActive === '활성' && `0.0625rem solid ${Theme.colors.G_3}`};
  padding: 0.3125rem 0.625rem;
  color: ${({ isActive }) =>
    isActive === '활성' ? Theme.colors.BLACK : Theme.colors.WHITE};
  border-radius: 0.5rem;
`;
